import React, { useContext, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import { useMediaQuery } from "react-responsive";
import * as Yup from "yup";
import { ThreeDots } from "react-loader-spinner";
import { Box, Flex, Heading, Stack, useColorModeValue } from "@chakra-ui/react";
import "./Login.css";
import toast from "react-hot-toast";
import TextField from "@mui/material/TextField";
import PasswordStrengthBar from "react-password-strength-bar";
import SignupWithGoogle from "./Google/SignupWithGoogle";
import { AuthContext } from "../../Context/AuthContextProvider";

const Signup = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const { setAuthAndPersist, setLoginUserAndPersist } = useContext(AuthContext);

  const isMobile = useMediaQuery({
    query: "(max-width: 1024px)",
  });

  const validationSchema = Yup.object().shape({
    firstName: Yup.string().required("First Name is required"),
    lastName: Yup.string().required("Last Name is required"),
    username: Yup.string().required("Username is required"),
    email_id: Yup.string().email("Invalid email").required("Email is required"),
    mobile_number: Yup.string()
      .required("Mobile number is required")
      .matches(/^[0-9]+$/, "Phone number must contain only digits")
      .matches(/^[6-9]/, "Phone number can only start with 9, 8, 7, or 6")
      .matches(/^\d{10}$/, "Phone number must be exactly 10 digits"),
    password: Yup.string()
      .min(8, "Password must be at least 8 characters")
      .matches(/[a-z]/, "Password must contain at least one lower case letter")
      .matches(/[A-Z]/, "Password must contain at least one upper case letter")
      .matches(/[0-9]/, "Password must contain at least one digit")
      .matches(
        /[!@#$%^&*(),.?":{}|<>]/,
        "Password must contain at least one special character"
      )
      .required("Password is required"),
  });

  const formik = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      username: "",
      email_id: "",
      mobile_number: "",
      password: "",
    },
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      setIsLoading(true);
      try {
        const submitData = {
          ...values,
        };

        const res = await fetch(
          "https://domains-marketplace-backend.vercel.app/sam/doc/users/register",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(submitData),
          }
        );
        const result = await res.json();
        if (res.ok) {
          // Save JWT token if provided by backend
          if (result.token) {
            localStorage.setItem("token", result.token);
          }

          toast.success("Signup successful");
          setAuthAndPersist(true);
          setLoginUserAndPersist(result.user);
          setIsLoading(false);
          resetForm();
          navigate("/Marketplace");
        } else {
          toast.error(result.message || "Signup failed");
          setIsLoading(false);
        }
      } catch (err) {
        toast.error("An error occurred during signup. Please try again.");
        setIsLoading(false);
      } finally {
        setIsLoading(false);
      }
    },
  });

  return (
    <Box className="Login-Page pattern-bg2">
      <Flex
        minH={"100vh"}
        align={"center"}
        justify={"center"}
        bg={useColorModeValue("gray.50", "gray.800")}
      >
        <Stack
          spacing={4}
          w={"80%"}
          maxW={"xl"}
          bg={useColorModeValue("transparent", "gray.700")}
          rounded={"xl"}
          p={6}
          my={12}
          borderRadius="none"
          className="Stack-box"
        >
          <div className="login-box-registration">
            <div className="flex flex-row justify-center">
              <Heading
                as="h1"
                textAlign="center"
                fontWeight="bold"
                className="main-titlee text-[#101c4c]"
                whiteSpace="nowrap"
              >
                Become a Member
              </Heading>
            </div>
            <form
              id="f"
              onSubmit={formik.handleSubmit}
              className="login-page-form"
            >
              <TextField
                size="small"
                required
                id="firstName"
                label="👤 First Name"
                variant="outlined"
                className="w-full !mb-[12px]"
                {...formik.getFieldProps("firstName")}
                error={
                  formik.touched.firstName && Boolean(formik.errors.firstName)
                }
                helperText={formik.touched.firstName && formik.errors.firstName}
              />
              <TextField
                size="small"
                required
                id="lastName"
                label="👤 Last Name"
                variant="outlined"
                className="w-full !mb-[12px]"
                {...formik.getFieldProps("lastName")}
                error={
                  formik.touched.lastName && Boolean(formik.errors.lastName)
                }
                helperText={formik.touched.lastName && formik.errors.lastName}
              />
              <TextField
                size="small"
                required
                id="username"
                label="📛 User Name"
                variant="outlined"
                className="w-full !mb-[12px]"
                {...formik.getFieldProps("username")}
                error={
                  formik.touched.username && Boolean(formik.errors.username)
                }
                helperText={formik.touched.username && formik.errors.username}
              />
              <div className="password-div-field w-full">
                <TextField
                  required
                  size="small"
                  id="email_id"
                  label="📨 Email"
                  variant="outlined"
                  className="w-full !mb-[12px]"
                  {...formik.getFieldProps("email_id")}
                  error={
                    formik.touched.email_id && Boolean(formik.errors.email_id)
                  }
                  helperText={formik.touched.email_id && formik.errors.email_id}
                />
              </div>
              <div className="password-div-field w-full">
                <TextField
                  required
                  size="small"
                  id="mobile_number"
                  label="📱 Mobile Number"
                  variant="outlined"
                  className="w-full !mb-[12px]"
                  {...formik.getFieldProps("mobile_number")}
                  error={
                    formik.touched.mobile_number &&
                    Boolean(formik.errors.mobile_number)
                  }
                  helperText={
                    formik.touched.mobile_number && formik.errors.mobile_number
                  }
                />
              </div>

              <div className="password-div-field w-full">
                <TextField
                  type="password"
                  size="small"
                  required
                  id="password"
                  label="🔑 Password"
                  variant="outlined"
                  className="w-full !mb-[12px] singup-password-input"
                  {...formik.getFieldProps("password")}
                  error={
                    formik.touched.password && Boolean(formik.errors.password)
                  }
                  helperText={formik.touched.password && formik.errors.password}
                />
              </div>
              {formik.values.password && (
                <PasswordStrengthBar password={formik.values.password} />
              )}
              <div
                fontSize={"18px"}
                fontWeight="600"
                color={"white"}
                border={"1px solid #AAB7C2"}
                className="w-full btn2 btn-color btn-l-r !py-[10px] !text-[16px]"
              >
                <button
                  type="submit"
                  className="join-btn custom-btn main-button w-full !rounded-[4px]"
                >
                  <div className="flex justify-center">
                    {isLoading ? (
                      <ThreeDots
                        color="#64432f"
                        height={25}
                        width={40}
                        timeout={1000000}
                      />
                    ) : (
                      "SIGNUP"
                    )}
                  </div>
                </button>
              </div>

              <Box className="">
                <div className="divider">Or</div>
              </Box>

              <SignupWithGoogle />
              {isMobile ? (
                <p className="text-center mb-2">
                  Already have an account?{" "}
                  <Link to="/login" className="text-[#2153ac] ">
                    Sign in
                  </Link>
                </p>
              ) : (
                <p></p>
              )}

              <p className="text-center">
                By Signing up to create an account,
                <br /> I accept{" "}
                <span className="text-[#2153ac]">
                  <Link to="/Terms">
                    Company&apos;s Terms of use and privacy policy
                  </Link>
                </span>
              </p>
            </form>
          </div>
        </Stack>
      </Flex>
    </Box>
  );
};

export default Signup;
